<template>
    <div class="single-track-players box">
        <div
            v-if="playerLoading"
            class="new-player-loader"
        >
            <base-loader />
        </div>
        <div
            v-if="isPageLoading"
            class="page-loader-overlay"
        >
            <base-loader />
        </div>
        <transition-group
            name="fade"
            mode="out-in"
        >
            <base-loader
                v-if="false === players"
                key="loader"
            />
            <base-listing-handler
                v-else
                key="shows-listing"
                v-slot:default="{
                    filteredItems,
                    pageLimit,
                    itemsCount,
                    totalPages,
                    sortOptions,
                    sortBy,
                    isReverse,
                    controlsChange,
                    paginationShowNav,
                    setPageNumber,
                    pageNum,
                    filters,
                }"
                :items="players"
                :sort-options="controls"
                :paginate="!pagination"
                :page-limit="15"
                :filter-function="featuredFilter"
            >
                <div class="box-title">
                    Single Track Players
                    <div v-if="!isSyncing" class="filters">
                        <base-checkbox
                            v-for="filter in filterListOptions"
                            :key="filter.type"
                            :values="filters"
                            :value="filter.value"
                            :label="filter.label"
                            :input-class="[`is-${filter.color}`, 'is-small']"
                            @change="value => controlsChange({ filters: value })"
                        />
                        <base-search-input
                            placeholder="Search episode..."
                            @input="value => controlsChange({ searchPhrase: value })"
                        />
                    </div>
                </div>
                <div v-if="isSyncing" class="box-content">
                    <div class="import-notice">
                        <div class="import-notice-icon">
                            <base-loader />
                        </div>
                        <div class="import-notice-content">
                            <h3>Episodes Syncing in Progress</h3>
                            <p>
                                Your show episodes are currently being synced from your RSS feed.
                                Episodes are being processed in the background. This may take a few
                                minutes depending on the number of episodes.
                            </p>
                            <p>
                                <strong>Please refresh the page to check if the sync is complete.</strong>
                            </p>
                        </div>
                    </div>
                </div>
                <div v-else class="box-content">
                    <div class="player-controls list-control">
                        <base-list-control
                            v-for="option in sortOptions"
                            :key="option.title"
                            :config="option"
                            :sort-by="sortBy"
                            :is-reverse="isReverse"
                            @change="value => controlsChange(value)"
                        />
                    </div>
                    <p v-if="0 === players.length || 0 === filteredItems.length">
                        No players to display
                    </p>
                    <ul
                        v-else
                        class="players-list list"
                    >
                        <single-track-player
                            v-for="player in filteredItems"
                            :key="player.id"
                            :player="player"
                            :show="show"
                            @open-embed="value => openEmbed(value)"
                            @edit-player="value => editPlayer(value)"
                        />
                    </ul>
                    <paginate
                        v-if="pagination ? pagination.lastPage > 1 : itemsCount > pageLimit"
                        :value="pagination ? pagination.currentPage : pageNum"
                        :page-count="pagination ? pagination.lastPage : totalPages"
                        :click-handler="pagination ? handlePageChange : setPageNumber"
                        :prev-text="''"
                        :next-text="''"
                        :class="[{ 'has-navigation': paginationShowNav } ,'pagination']"
                        :active-class="'is-active'"
                        :prev-link-class="'pagination-arrow pagination-prev'"
                        :next-link-class="'pagination-arrow pagination-next'"
                        :hide-prev-next="paginationShowNav"
                        :first-last-button="paginationShowNav"
                        :first-button-text="''"
                        :last-button-text="''"
                        :break-view-class="'pagination-decorator'"
                    />
                </div>
            </base-listing-handler>
        </transition-group>
    </div>
</template>

<script>
import Paginate from 'vuejs-paginate';
import SingleTrackPlayer from './SingleTrackPlayer';
import { featuredFilter } from '@/utils/listFilters';
import { Status } from '@/utils/show';
import toasts from '@/mixins/toasts';

export default {
    components: {
        Paginate,
        SingleTrackPlayer,
    },

    mixins: [toasts],

    props: {
        players: {
            type: [Array, Boolean],
            default: () => [],
        },
        show: {
            type: Object,
            required: true,
        },
        playerLoading: {
            type: Boolean,
            required: true,
        },
        pagination: {
            type: Object,
            default: null,
        },
        isPageLoading: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        controls: [
            {
                listLabel: 'No.',
                value: 'trackNo',
                iteratees: [
                    item => parseInt(item.trackNo.split(':')[0]),
                    item => parseInt(item.trackNo.split(':')[1]),
                ],
                isReverse: true,
            },
            {
                listLabel: 'Episode Title',
                value: 'title',
            },
        ],
        filterListOptions: [
            {
                value: 'featured',
                label: 'Featured',
                color: 'yellow',
            },
        ],
    }),

    computed: {
        /**
         * Check if show is currently syncing
         *
         * @returns {boolean} True if sync in progress
         */
        isSyncing() {
            return this.show.status === Status.Syncing;
        },
    },

    methods: {
        featuredFilter,

        /**
         * Opens track modal
         *
         * @param   {object}  track    Track data
         */
        editPlayer(track) {
            this.$emit('click', {
                editPlayerType: 'track',
                track,
            });
        },

        /**
         * Opens embed
         *
         * @param   {object}  track    Track data
         */
        openEmbed(track) {
            this.$emit('click', {
                editPlayerType: 'track',
                showEmbed: true,
                track,
            });
        },

        /**
         * Handle server-side page change
         *
         * @param   {number}  page    Page number
         */
        handlePageChange(page) {
            this.$emit('page-change', page);
        },
    },
};
</script>
